import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import XCircle from "@insite/mobius/Icons/XCircle";
import { Hidden } from "@insite/mobius/index";
import Modal from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ButtonIcon, ButtonIconPresentationProps } from "@waytek/MobiusOverrides/Button/Button";
import DatePicker, {
    DatePickerPresentationProps,
    DatePickerState,
} from "@waytek/MobiusOverrides/DatePicker/DatePicker";
import { ModalPresentationProps } from "@waytek/MobiusOverrides/Modal/Modal";
import Tooltip, { TooltipPresentationProps } from "@waytek/MobiusOverrides/Tooltip/Tooltip";
import { mediaQueriesBP } from "@waytek/Utilities/variables";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    key: string;
    cart: Cart;
    cartLine?: CartLineModel;
    modalIsOpen: boolean;
    handleModalClose: () => void;
    handleModalSave: () => void;
    cartLineQty: string;
    handleCartLineChange: (event: any) => void;
    handleCartLineKeyDown: (event: any) => void;
    handleCartLineBlur: () => void;
    shipments?: any[];
    handleShipmentChange: (item: any, idx: number) => void;
    handleAddRemoveShipmentLine: (idx: number, isAdd: boolean) => void;
    extendedStyles?: CartLineScheduleShipmentModalStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartSettings, customSettings } = getSettingsCollection(state);
    return {
        cartSettings,
        customSettings: customSettings as any,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartLineScheduleShipmentModalStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    extendedUnitNetPriceText?: TypographyProps;
    productPrice?: ProductPriceStyles;
    subtotalWrapper?: InjectableCss;
    productBrand?: ProductBrandStyles;
    productNumbersMobile?: InjectableCss;
    productMinQtyStockItem?: InjectableCss;
    productMinQtyStockLabel?: TypographyPresentationProps;
    productMinQtyStockValue?: TypographyPresentationProps;
    productMultipleStockItem?: InjectableCss;
    productMultipleStockLabel?: TypographyPresentationProps;
    productMultipleStockValue?: TypographyPresentationProps;
    productStockWrapper?: InjectableCss;
    productExcedeeWrapper?: InjectableCss;
    productMultipleGlobalWrapper?: InjectableCss;
    subtotalLabel?: TypographyPresentationProps;
    modalWrapper?: ModalPresentationProps;
    modalTopSection?: InjectableCss;
    modalImageGridItem?: GridItemProps;
    modalProductDescriptionGridItem?: GridItemProps;
    modalQuantityGridItem?: GridItemProps;
    shipmentsWrapper?: InjectableCss;
    modalBottomSection?: InjectableCss;
    modalPriceWrapper?: InjectableCss;
    modalQuantityWrapper?: InjectableCss;
    modalSectionTitle?: TypographyPresentationProps;
    modalBackButton?: ButtonPresentationProps;
    modalContinueButton?: ButtonPresentationProps;
    modalBottomText?: GridItemProps;
    modalBottomControls?: GridItemProps;
    editableQuantityTextField?: TextFieldPresentationProps;
    shipmentDatePicker?: DatePickerPresentationProps;
    addRemoveButton?: ButtonPresentationProps;
    addRemoveButtonIcon?: ButtonIconPresentationProps;
    errorMessageGrid?: GridItemProps;
    errorMessage?: TypographyProps;
    secondColumnHeaders?: GridItemProps;
    formWrapper?: InjectableCss;
    tooltip?: TooltipPresentationProps;
}

export const cartLineScheduleShipmentModalStyles: CartLineScheduleShipmentModalStyles = {
    formWrapper: {
        css: css`
            padding-bottom: 80px;

            ${mediaQueriesBP.maxMD} {
                padding-bottom: 160px;
            }
        `,
    },
    shipmentsWrapper: {
        css: css`
            padding: 0;
            display: flex;
            flex-wrap: wrap;
            height: 154px;
            overflow-y: auto;
        `,
    },
    modalBottomSection: {
        css: css`
            &&& {
                border-top: 1px solid ${getColor("common.global.industrialTaupe")};
                padding: 20px;
                position: absolute;
                left: 0;
                bottom: 0;
                background: ${getColor("common.global.white")};
                width: 100%;
            }
        `,
    },

    secondColumnHeaders: {
        css: css`
            ${mediaQueriesBP.maxMD} {
                display: none;
            }
        `,
    },

    tooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                margin-right: 5px;

                button {
                    span {
                        color: ${getColor("common.global.guardianBlue")};
                    }
                }

                p {
                    text-align: left;
                }
            `,
        },
    },
};

const StyledForm = getStyledWrapper("form");

// eslint-disable-next-line
/* eslint-disable sonarjs/cognitive-complexity */
const CartLinesScheduleShipmentModal: FC<Props> = ({
    key,
    cart,
    cartLine,
    modalIsOpen,
    handleModalClose,
    handleModalSave,
    cartLineQty,
    handleCartLineChange,
    handleCartLineKeyDown,
    handleCartLineBlur,
    shipments = [],
    handleShipmentChange,
    handleAddRemoveShipmentLine,
    extendedStyles,
    cartSettings,
    customSettings,
}) => {
    const [styles] = React.useState(() => mergeToNew(cartLineScheduleShipmentModalStyles, extendedStyles));
    const [showFormErrors, setShowFormErrors] = React.useState(false);
    const [qtyExceeded, setQtyExceeded] = React.useState(false);
    const [globalMultipleError, setGlobalMultipleError] = React.useState(false);

    if (!cartLine) {
        return null;
    }

    const multipleQty = +(cartLine.properties["multipleSaleQty"] ?? "0");
    const isMultipleRule = cartLine.properties["roundingRule"]?.toLowerCase() === "multiple only";

    const validateForm = () => {
        if (qtyExceeded) {
            return true;
        }

        for (let idx = 0; idx < shipments.length; idx++) {
            const item = shipments[idx];
            if (item.disabled) {
                continue;
            }

            const { hasAnyError } = checkErrorOnItem(item, idx);
            if (hasAnyError) {
                return false;
            }
        }
        return true;
    };

    const handleSubmit = ($ev: React.FormEvent<HTMLFormElement>) => {
        $ev.preventDefault();

        if (validateForm()) {
            handleModalSave();
            setShowFormErrors(false);
        } else {
            setShowFormErrors(true);
        }
    };

    const checkErrorOnItem = (item: any, idx: number) => {
        const previousValues = shipments
            .filter((_, shipmentIdx) => shipmentIdx < idx)
            .map(prevItems => prevItems.qty)
            .reduce((val, total) => val + total, 0);
        const minError = +item.qty < +cartLine.properties["minimumOrderQty"];
        const greaterError = +item.qty > +cartLineQty;
        const sumError = +item.qty + previousValues > +cartLineQty;
        const sumAllError = shipments.map(line => line.qty).reduce((val, total) => val + total, 0) !== +cartLineQty;

        const dateRequiredError = item.date == null;
        const dateOptions: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" };
        const dateTimeFormat = new Intl.DateTimeFormat("en-US", dateOptions);
        const sameDateError = dateRequiredError
            ? false
            : shipments
                  .filter((_, shipmentIdx) => shipmentIdx !== idx)
                  .filter(shipment => shipment.date != null && `${shipment.date}`.trim() !== "")
                  .map(shipment => new Date(shipment.date))
                  .find(date => dateTimeFormat.format(date) === dateTimeFormat.format(new Date(item.date))) != null;

        let multipleOfError = false;
        if (multipleQty && isMultipleRule) {
            multipleOfError = +item.qty % multipleQty !== 0;
        }
        const hasAnyError =
            (minError ||
                greaterError ||
                sumError ||
                dateRequiredError ||
                sumAllError ||
                multipleOfError ||
                globalMultipleError ||
                sameDateError) &&
            !item.disabled;

        return {
            minError,
            greaterError,
            sumError,
            sumAllError,
            multipleOfError,
            hasAnyError,
            dateRequiredError,
            sameDateError,
        };
    };

    // eslint-disable-next-line
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const mapShipments = (item: any, idx: number) => {
        const {
            minError,
            greaterError,
            sumError,
            sumAllError,
            multipleOfError,
            hasAnyError,
            dateRequiredError,
            sameDateError,
        } = checkErrorOnItem(item, idx);
        let todaysDate = new Date();

        if (todaysDate.getHours() > +(customSettings.cutOfTimeForShipments || 15)) {
            todaysDate = addDays(todaysDate, 1);
        }
        todaysDate.setHours(0);
        todaysDate.setMinutes(0);
        todaysDate.setSeconds(0);
        todaysDate.setMilliseconds(0);

        return (
            <>
                {/* eslint-disable-next-line react/no-array-index-key */}
                <GridItem width={4} key={`schedule_qty_col_${idx}_${key}`}>
                    <TextField
                        {...styles.editableQuantityTextField}
                        key={`schedule_qty_${idx}_${key}`}
                        value={item.qty}
                        onChange={(event: any) => {
                            // eslint-disable-next-line no-restricted-globals
                            if (isNaN(+event.currentTarget.value) || event.currentTarget.value.length > 1000000000) {
                                event.preventDefault();
                            } else {
                                handleShipmentChange({ ...item, qty: +event.currentTarget.value }, idx);
                            }
                        }}
                        data-test-selector={`schedule_qty_${idx}_${key}`}
                        disabled={item.disabled || qtyExceeded}
                        required
                        error={showFormErrors && hasAnyError && <></>}
                    />
                </GridItem>
                {/* eslint-disable-next-line react/no-array-index-key */}
                <GridItem width={8} key={`schedule_date_col_${idx}_${key}`}>
                    <DatePicker
                        {...styles.shipmentDatePicker}
                        key={`schedule_date_${idx}_${key}`}
                        selectedDay={item.date != null ? new Date(item.date) : undefined}
                        dateTimePickerProps={{
                            minDate: todaysDate,
                            maxDate: addDays(todaysDate, 365),
                            monthAriaLabel: translate("Scheduled shipment date month"),
                            dayAriaLabel: translate("Scheduled shipment date day"),
                            yearAriaLabel: translate("Scheduled shipment date year"),
                            ...styles.shipmentDatePicker?.dateTimePickerProps,
                        }}
                        onDayChange={({ selectedDay: date }: DatePickerState) =>
                            handleShipmentChange({ ...item, date }, idx)
                        }
                        disabled={item.disabled || qtyExceeded}
                    />
                    {idx === shipments.length - 1 && idx < 11 && (
                        <Button
                            {...styles.addRemoveButton}
                            key={`schedule_add_${idx}_${key}`}
                            onClick={() => handleAddRemoveShipmentLine(idx, true)}
                            disabled={qtyExceeded}
                            type="button"
                        >
                            <ButtonIcon {...styles.addRemoveButtonIcon} src={PlusCircle}></ButtonIcon>
                        </Button>
                    )}
                    {(idx === 0 || idx === 11 || idx < shipments.length - 1) && (
                        <Button
                            {...styles.addRemoveButton}
                            key={`schedule_del_${idx}_${key}`}
                            onClick={() => handleAddRemoveShipmentLine(idx, false)}
                            disabled={idx === 0 || qtyExceeded}
                            type="button"
                        >
                            <ButtonIcon {...styles.addRemoveButtonIcon} src={XCircle}></ButtonIcon>
                        </Button>
                    )}
                </GridItem>

                {!qtyExceeded && showFormErrors && hasAnyError && (
                    <GridItem {...styles.errorMessageGrid} key={`schedule_error_col_${idx}_${key}`}>
                        <Typography {...styles.errorMessage} key={`schedule_error_label_${idx}_${key}`}>
                            {minError && (
                                <StyledWrapper>{siteMessage("Checkout_ScheduledShipment_MinOrderError")}</StyledWrapper>
                            )}

                            {!minError && greaterError && (
                                <StyledWrapper>
                                    {siteMessage("Checkout_ScheduledShipment_GreaterThanGlobalQtyError")}
                                </StyledWrapper>
                            )}

                            {!minError && !greaterError && sumError && (
                                <StyledWrapper>
                                    {siteMessage("Checkout_ScheduledShipment_GreaterThanAllPrevQtyError")}
                                </StyledWrapper>
                            )}

                            {!minError && !greaterError && !sumError && sumAllError && (
                                <StyledWrapper>
                                    {siteMessage("Checkout_ScheduledShipment_GreaterThanAllQtyError")}
                                </StyledWrapper>
                            )}

                            {!minError && !greaterError && !sumError && !sumAllError && multipleOfError && (
                                <StyledWrapper>
                                    {siteMessage("Checkout_ScheduledShipment_MultipleNotSatisfiedError")}
                                </StyledWrapper>
                            )}

                            {!minError &&
                                !greaterError &&
                                !sumError &&
                                !sumAllError &&
                                !multipleOfError &&
                                dateRequiredError && (
                                    <StyledWrapper>
                                        {siteMessage("Checkout_ScheduledShipment_DateRequiredError")}
                                    </StyledWrapper>
                                )}

                            {!minError &&
                                !greaterError &&
                                !sumError &&
                                !sumAllError &&
                                !multipleOfError &&
                                !dateRequiredError &&
                                sameDateError && (
                                    <StyledWrapper>
                                        {siteMessage("Checkout_ScheduledShipment_SameDateError")}
                                    </StyledWrapper>
                                )}
                        </Typography>
                    </GridItem>
                )}
            </>
        );
    };

    const handleQtyChanged = (event: any) => {
        const { value } = event.currentTarget;
        handleCartLineChange(event);
        setQtyExceeded(+value > cartLine.qtyOnHand);
        if (multipleQty && isMultipleRule) {
            setGlobalMultipleError(+value % multipleQty !== 0);
        }
    };

    const handleQtyBlur = () => {
        handleCartLineBlur();
        setQtyExceeded(+cartLineQty > cartLine.qtyOnHand);
        if (multipleQty && isMultipleRule) {
            setGlobalMultipleError(+cartLineQty % multipleQty !== 0);
        }
    };

    return (
        <>
            <Modal
                headline={translate("Schedule Shipment")}
                isOpen={modalIsOpen}
                handleClose={handleModalClose}
                data-test-selector={key}
                {...styles.modalWrapper}
            >
                <StyledForm
                    id="cartLineScheduleShipmentForm"
                    onSubmit={handleSubmit}
                    noValidate={true}
                    data-test-selector="cartLineScheduleShipmentForm"
                    {...styles.formWrapper}
                >
                    <GridContainer {...styles.modalTopSection}>
                        <GridItem {...styles.modalImageGridItem}>
                            <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                        </GridItem>
                        <GridItem {...styles.modalProductDescriptionGridItem}>
                            {cartLine.brand && (
                                <StyledWrapper>
                                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                </StyledWrapper>
                            )}
                            <StyledWrapper>
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                            </StyledWrapper>

                            <StyledWrapper className="modalDescriptionItem">
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </StyledWrapper>

                            <StyledWrapper className="modalDescriptionItem" {...styles.productMinQtyStockItem}>
                                <Typography {...styles.productMinQtyStockLabel}>
                                    {translate("Min. Order Qty: ")}
                                </Typography>
                                <Typography {...styles.productMinQtyStockValue}>
                                    {cartLine.properties["minimumOrderQty"] || 1}
                                </Typography>
                            </StyledWrapper>

                            {isMultipleRule && (
                                <StyledWrapper className="modalDescriptionItem" {...styles.productMultipleStockItem}>
                                    <Typography {...styles.productMultipleStockLabel}>
                                        {translate("Sold in Multiples of")}:
                                    </Typography>
                                    <Typography {...styles.productMultipleStockValue}>{multipleQty}</Typography>
                                </StyledWrapper>
                            )}

                            <StyledWrapper className="modalDescriptionItem" {...styles.productStockWrapper}>
                                <Typography {...styles.productMinQtyStockValue}>
                                    {cartLine.qtyOnHand || 0} {translate("in Stock")}
                                </Typography>
                            </StyledWrapper>

                            {qtyExceeded && (
                                <StyledWrapper className="modalDescriptionItem" {...styles.productExcedeeWrapper}>
                                    <Typography {...styles.errorMessage}>
                                        <StyledWrapper>
                                            {siteMessage("Checkout_ScheduledShipment_QtyExceeded")}
                                        </StyledWrapper>
                                    </Typography>
                                </StyledWrapper>
                            )}
                        </GridItem>
                        <GridItem {...styles.modalQuantityGridItem}>
                            <StyledWrapper className="mobileInfo productNumber" {...styles.productNumbersMobile}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </StyledWrapper>

                            <StyledWrapper
                                className="mobileInfo productMinQtyStockItem"
                                {...styles.productMinQtyStockItem}
                            >
                                <Typography {...styles.productMinQtyStockLabel}>
                                    {translate("Min. Order Qty: ")}
                                </Typography>
                                <Typography {...styles.productMinQtyStockValue}>{cartLine.qtyOnHand || 1}</Typography>
                            </StyledWrapper>

                            <StyledWrapper className="mobileInfo stockinfo" {...styles.productStockWrapper}>
                                <Typography {...styles.productMinQtyStockValue}>
                                    {cartLine.qtyOnHand || 0} {translate("in Stock")}
                                </Typography>
                            </StyledWrapper>

                            {!cart.cartNotPriced && (
                                <StyledWrapper className="priceInfo" {...styles.modalPriceWrapper}>
                                    <ProductPrice
                                        product={cartLine}
                                        currencySymbol={cart.currencySymbol}
                                        showSavings={false}
                                        showSavingsAmount={false}
                                        showSavingsPercent={false}
                                        showInvalidPriceMessage={true}
                                        extendedStyles={styles.productPrice}
                                    />
                                </StyledWrapper>
                            )}

                            <StyledWrapper {...styles.modalQuantityWrapper}>
                                <TextField
                                    {...styles.editableQuantityTextField}
                                    type="number"
                                    min={0}
                                    max={999999}
                                    label={<>{translate("Qty")}:</>}
                                    value={cartLineQty}
                                    onChange={handleQtyChanged}
                                    onKeyDown={handleCartLineKeyDown}
                                    onBlur={handleQtyBlur}
                                    onClick={(event: any) => event.currentTarget.focus()}
                                    data-test-selector={`cartline_qty_${key}`}
                                    error={(qtyExceeded || globalMultipleError) && <></>}
                                />

                                {globalMultipleError && !qtyExceeded && (
                                    <Hidden
                                        below="md"
                                        className="modalDescriptionItem"
                                        {...styles.productMultipleGlobalWrapper}
                                    >
                                        <Typography {...styles.errorMessage}>
                                            <StyledWrapper>
                                                {siteMessage("Checkout_ScheduledShipment_MultipleOfGlobalError")}
                                            </StyledWrapper>
                                        </Typography>
                                    </Hidden>
                                )}

                                {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && (
                                    <StyledWrapper {...styles.subtotalWrapper}>
                                        <Tooltip
                                            {...styles.tooltip}
                                            text={siteMessage("Schedule_Shipment_Modal_Subtotal_Tooltip") as string}
                                        />
                                        <Typography {...styles.subtotalLabel} as="p">
                                            {translate("Subtotal")}:
                                        </Typography>
                                        <Typography
                                            {...styles.extendedUnitNetPriceText}
                                            data-test-selector="cartline_extendedUnitNetPrice"
                                        >
                                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>

                            {globalMultipleError && !qtyExceeded && (
                                <Hidden
                                    above="sm"
                                    className="modalDescriptionItem"
                                    {...styles.productMultipleGlobalWrapper}
                                >
                                    <Typography {...styles.errorMessage}>
                                        <StyledWrapper>
                                            {siteMessage("Checkout_ScheduledShipment_MultipleOfGlobalError")}
                                        </StyledWrapper>
                                    </Typography>
                                </Hidden>
                            )}
                        </GridItem>
                    </GridContainer>

                    <GridContainer gap={20}>
                        <GridItem width={12}>
                            <Typography {...styles.modalSectionTitle} as="p">
                                {translate("Schedule Shipments")}
                            </Typography>
                        </GridItem>
                        <StyledWrapper {...styles.shipmentsWrapper}>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <GridContainer gap={15}>
                                    <GridItem width={4}>
                                        <Typography>{translate("Qty")}</Typography>
                                    </GridItem>
                                    <GridItem width={8}>
                                        <Typography>{translate("Date")}</Typography>
                                    </GridItem>
                                    {shipments.filter((_, idx) => idx < 6).map(mapShipments)}
                                </GridContainer>
                            </GridItem>

                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <GridContainer gap={15}>
                                    {shipments.length > 6 && (
                                        <>
                                            <GridItem {...styles.secondColumnHeaders} width={4}>
                                                <Typography>{translate("Qty")}</Typography>
                                            </GridItem>
                                            <GridItem {...styles.secondColumnHeaders} width={8}>
                                                <Typography>{translate("Date")}</Typography>
                                            </GridItem>
                                        </>
                                    )}
                                    {shipments
                                        .filter((_, idx) => idx >= 6)
                                        .map((item, idx) => mapShipments(item, idx + 6))}
                                </GridContainer>
                            </GridItem>
                        </StyledWrapper>
                    </GridContainer>

                    <GridContainer {...styles.modalBottomSection}>
                        <GridItem {...styles.modalBottomText}>
                            <Typography>{siteMessage("ScheduleShipmentModal_Disclaimer")}</Typography>
                        </GridItem>
                        <GridItem {...styles.modalBottomControls}>
                            <Button {...styles.modalBackButton} onClick={handleModalClose}>
                                {translate("Back")}
                            </Button>
                            <Button
                                disabled={cartLineQty === "0" || cartLineQty === ""}
                                {...styles.modalContinueButton}
                                type="submit"
                            >
                                {translate("Continue")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </Modal>
        </>
    );
};
// eslint-disable-next-line
/* eslint-enable sonarjs/cognitive-complexity */

export default connect(mapStateToProps)(CartLinesScheduleShipmentModal);
